import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { WizIcon, WizPopUp } from '@wizeoo/akm-component-lib';
import { BusinessException, EntityModificationPack } from '@wizeoo/akm-message-lib';
import { StaticFeatureEnum } from '../../manager/permission/permissions.types';
import { FEATURE_PERIMETER } from '../../model/enums';
import { ProfileVO } from '../../vo/user/profile.vo';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';
import { EMEngine } from '../entities/entity-modification.engine';
import { ViewData } from '../../model/view.data';
import { CommitCmd } from '../../command/em/commit.cmd';
import { cloneDeep } from 'lodash-es';
import { UserVO } from '../../vo/user/user.vo';

/**
 * mémorisation des modifs de collections de users sur les profils
 */
export interface ProfileUpdate {
  key: string, // la clé unique du profil
  profil: ProfileVO, // le profil source
  updates: Map<string, ProfileUserUpdate>// les ajouts suppression de users, clé = mail
}

export interface ProfileUserUpdate {
  user: UserVO, // le user
  operation: string // Create or Delete
}


/** gestion des profils utilisateurs... */
export class ProfileEngine {
  
  protected model: Model = null;
  constructor(model: Model) {
    this.model = model;
  }

  /**
   * Retourne la MEME instance de profil que dans la liste du modèle.
   * @param source : ProfileVO cherché
   */
  public getCurrentInstance(source: ProfileVO): ProfileVO {
    let result: ProfileVO = null;

    // recherche d'une entrée de contexte utilisateur (key = code country)
    if (!source) {
      // seulement si recherche vide
      const contextProfil: string = this.model.userContextService.getTab(StaticFeatureEnum.PROFILE)?.selectedItemId;
      if (contextProfil) {
        source = new ProfileVO(this.model);
        source.code = contextProfil;
      }
    }

    if (source && this.model.profileMap) {
      result = this.model.profileMap.get(this.model.businessUnitCode)?.get(this.model.visionCode)?.get(source.code);
    }
    return result;
  }

  /**
   * Création d'un profil
   * @return ProfileVO Le profil créé
   */
  public createObjectProfile(): ProfileVO {
    const profile = new ProfileVO(this.model);
    profile.domain = this.model.domainCode;
    profile.code = null;
    profile.label = null;
    profile.businessUnitCode = this.model.businessUnitCode;
    profile.visionCode = this.model.visionCode;
    // On peut seulement créer des profiles sur le périmètre APP
    profile.perimeter = FEATURE_PERIMETER.APP;
    return profile;
  }

  /**
   * Clone de profil
   * @param profile Le profil à cloner
   * @return ProfileVO Le clone du profil
   */
  public clone(profile: ProfileVO): ProfileVO {
    let result: ProfileVO = null;
    if(!!profile) {
      result = new ProfileVO(this.model, cloneDeep(profile.source));
    }
    return result;
  }

  /**
   * Vérification que l'object profile est valid (attribut code et businessUnitCode)
   * @param profile Le profil testé
   * @return boolean true s'il est valide, false sinon
   */
  public isValid(profile: ProfileVO): boolean {
    if (!profile) {
      return false;
    }

    if (profile.code === undefined || profile.code === null) {
      return false;
    }

    if (profile.domain === undefined || profile.domain === null) {
      return false;
    }

    if (profile.businessUnitCode === undefined || profile.businessUnitCode === null) {
      return false;
    }

    return true;
  }

  /**
   * create/ update d'un pays
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: ProfileVO, from: ProfileVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to, WIZ_COMMAND_STATE.SUCCESS); // retour en vue
    }
  }

  /**
   * delete d'un profil
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: ProfileVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: ProfileVO = cmd.sourceVO[0] as ProfileVO;
      // une seule modification à la fois, packEntry donne l'objet
      const source: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = source; // le clone
            const modelProfile: ProfileVO = this.model.profileMap.get(vo.businessUnitCode).get(vo.visionCode).get(source.code);
            if (!!modelProfile) {
              modelProfile.source = source;
            }
            break;
          case EM_ACTION.CREATE:
            vo.source = source;
            if (!this.model.profileMap.has(vo.businessUnitCode)) {
              this.model.profileMap.set(vo.businessUnitCode, new Map());
            }
            if (!this.model.profileMap.get(vo.businessUnitCode).has(vo.visionCode)) {
              this.model.profileMap.get(vo.businessUnitCode).set(vo.visionCode, new Map());
            }

            this.model.profileMap.get(vo.businessUnitCode).get(vo.visionCode).set(vo.code, vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = source;
            this.model.profileMap.get(vo.businessUnitCode).get(vo.visionCode).delete(vo.code);
            break;
        }
      }
      cmd.viewCallBack(vo, packStatus); // retour en vue
    }
  }

  public getProfileKey(prof: ProfileVO): string {
    return [prof.domain, prof.businessUnitCode, prof.visionCode, prof.code].join(ViewData.KEY_SEPARATOR);
  }

  public get profilesList(): ProfileVO[] {
    let result: ProfileVO[] = [];
    // TODO TODO  optimiser. Inutile de boucler sur tout
    let srcMap: Map<string, Map<string, Map<string, ProfileVO>>> = this.model.profileMap;
    [...srcMap.values()].forEach(srcSubMap  => {
      [...srcSubMap.values()].forEach(srcSubMap2  => {
        [...srcSubMap2.values()].forEach(prof  => {
          result.push(prof);
        });
      });
    });

    return result;
  }

}
